import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClassPicker } from "@/components/ClassPicker";
import { AuthImage } from "@/components/AuthImage";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSectionsEnabled } from "@/lib/sections";

export function StudentPicker({ value, onChange }) {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const useSec = useSectionsEnabled();
  const [classes, setClasses] = useState([]);
  const [cls, setCls] = useState({ branch_id: user?.branch_id || "", class_name: "", section: "" });
  const [students, setStudents] = useState([]);
  useEffect(() => { if (isTeacher) api.get("/my-classes").then((r) => { setClasses(r.data); if (r.data[0]) setCls({ branch_id: user.branch_id, ...r.data[0] }); }).catch(() => {}); }, [isTeacher]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!cls.class_name || !cls.section) { setStudents([]); return; }
    api.get("/class-students", { params: cls }).then((r) => setStudents(r.data)).catch(() => setStudents([]));
  }, [cls.branch_id, cls.class_name, cls.section]);
  return (
    <div className="space-y-3">
      {isTeacher ? (
        <div><Label>{useSec ? "Class & section" : "Class"}</Label>
          <Select value={cls.class_name ? `${cls.class_name}|${cls.section}` : ""} onValueChange={(v) => { const [c, s] = v.split("|"); setCls({ branch_id: user.branch_id, class_name: c, section: s }); onChange(""); }}>
            <SelectTrigger data-testid="su-class" className="mt-1 sm:w-64"><SelectValue placeholder={classes.length ? "Choose class" : "No class assigned"} /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.class_name + c.section} value={`${c.class_name}|${c.section}`}>{c.class_name}{useSec ? ` - ${c.section}` : ""}{c.student_count != null ? ` (${c.student_count} students)` : ""}</SelectItem>)}</SelectContent>
          </Select></div>
      ) : <ClassPicker value={cls} onChange={(v) => { setCls(v); onChange(""); }} />}
      <div><Label>Student</Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger data-testid="su-student" className="mt-1 sm:w-64"><SelectValue placeholder={students.length ? "Choose student" : "No students in this class"} /></SelectTrigger>
          <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.admission_number ? ` (${s.admission_number})` : ""}</SelectItem>)}</SelectContent>
        </Select></div>
    </div>
  );
}

export function ClassSelect({ value, onChange }) {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const useSec = useSectionsEnabled();
  const [classes, setClasses] = useState([]);
  useEffect(() => { if (isTeacher) api.get("/my-classes").then((r) => { setClasses(r.data); if (r.data[0] && !value.class_name) onChange({ branch_id: user.branch_id, ...r.data[0] }); }).catch(() => {}); }, [isTeacher]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isTeacher) return <ClassPicker value={value} onChange={onChange} />;
  return (
    <div><Label>{useSec ? "Class & section" : "Class"}</Label>
      <Select value={value.class_name ? `${value.class_name}|${value.section}` : ""} onValueChange={(v) => { const [c, s] = v.split("|"); onChange({ branch_id: user.branch_id, class_name: c, section: s }); }}>
        <SelectTrigger data-testid="bulk-class" className="mt-1 sm:w-64"><SelectValue placeholder={classes.length ? "Choose class" : "No class assigned"} /></SelectTrigger>
        <SelectContent>{classes.map((c) => <SelectItem key={c.class_name + c.section} value={`${c.class_name}|${c.section}`}>{c.class_name}{useSec ? ` - ${c.section}` : ""}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

export function ChildChips({ children, value, onChange }) {
  if (children.length < 2) return null;
  return (
    <div className="flex gap-2 mb-5 overflow-x-auto">
      {children.map((c) => (
        <button key={c.id} data-testid={`child-chip-${c.id}`} onClick={() => onChange(c.id)}
          className={`flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border transition-colors ${value === c.id ? "bg-violet-600 text-white border-violet-600" : "bg-white border-violet-100"}`}>
          {c.photo_url ? <AuthImage src={c.photo_url} alt={c.name} className="h-8 w-8 rounded-full object-cover" /> : <span className="h-8 w-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold">{c.name[0]}</span>}
          <span className="text-sm font-semibold">{c.name}</span>
        </button>
      ))}
    </div>
  );
}
